import {
  type CollisionDetection,
  DndContext,
  type DragMoveEvent,
  KeyboardCode,
  type KeyboardCoordinateGetter,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { HomeWidget, WidgetArea } from "@ronin/contracts";
import { fitsAt, type GridRect, HOME_GRID_COLUMNS, HOME_GRID_MAX_ROWS } from "@ronin/core";
import { cn, toast } from "@ronin/ui";
import { Trash2 } from "lucide-react";
import { createContext, type ReactNode, useContext, useRef, useState } from "react";
import { WidgetTile, widgetLabel } from "./widget-tile.js";

/** FR9: rows of 0.5rem (with the 0.5rem gap, heights step by 1rem); a link is 4 rows (3.5rem). */
const ROW_REM = 0.5;

export const rectOf = (widget: Pick<HomeWidget, "x" | "y" | "w" | "h">): GridRect => ({
  x: widget.x,
  y: widget.y,
  w: widget.w,
  h: widget.h,
});

/** Column width and row height plus the gap, in px, measured from a grid element. */
function cellSteps(grid: HTMLElement | null | undefined) {
  if (!grid) return { x: 80, y: 32 };
  const style = getComputedStyle(grid);
  const gap = Number.parseFloat(style.columnGap) || 8;
  const column = (grid.clientWidth - gap * (HOME_GRID_COLUMNS - 1)) / HOME_GRID_COLUMNS;
  const row = Number.parseFloat(style.fontSize) * ROW_REM || 8;
  return { x: column + gap, y: row + gap };
}

/** The pointer picks the grid; the keyboard (no pointer) uses the tile's overlap. */
const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : rectIntersection(args);
};

type Target = GridRect & { id: string; area: WidgetArea; free: boolean };
export type PlacePatch = { x?: number; y?: number; w?: number; h?: number; area?: WidgetArea };

interface TileDndState {
  target: Target | null;
  register: (area: WidgetArea, element: HTMLElement | null) => void;
  resize: (widget: HomeWidget, dims: { w: number; h: number }) => void;
}

const TileDndContext = createContext<TileDndState | null>(null);

/**
 * spec12 FR9, FR9a: one drag and drop for both grids. A tile follows the pointer; the grid
 * under it shows a dashed outline on the cells it lands on (red when taken); dropping moves
 * it there, also from one grid into the other. Mouse: drag after 4 px. Touch: hold 250 ms.
 * Keyboard: Space lifts, arrows move one cell, Space drops, Escape cancels.
 */
export function TileDnd({
  grids,
  onPlace,
  onDelete,
  children,
}: {
  grids: Record<WidgetArea, HomeWidget[]>;
  onPlace: (widget: HomeWidget, patch: PlacePatch) => void;
  /** FR8: a tile dropped on the delete zone (which shows while dragging). */
  onDelete: (widget: HomeWidget) => void;
  children: ReactNode;
}) {
  const elements = useRef<Partial<Record<WidgetArea, HTMLElement>>>({});
  const [target, setTarget] = useState<Target | null>(null);
  const [dragging, setDragging] = useState<HomeWidget | null>(null);
  const byId = new Map([...grids.top, ...grids.board].map((w) => [w.id, w]));
  const steps = () => cellSteps(elements.current.board ?? elements.current.top);

  /** Arrow keys move the lifted tile by exactly one cell. */
  const coordinateGetter: KeyboardCoordinateGetter = (event, { currentCoordinates }) => {
    const step = steps();
    switch (event.code) {
      case KeyboardCode.Right:
        return { ...currentCoordinates, x: currentCoordinates.x + step.x };
      case KeyboardCode.Left:
        return { ...currentCoordinates, x: currentCoordinates.x - step.x };
      case KeyboardCode.Down:
        return { ...currentCoordinates, y: currentCoordinates.y + step.y };
      case KeyboardCode.Up:
        return { ...currentCoordinates, y: currentCoordinates.y - step.y };
      default:
        return undefined;
    }
  };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
      // Enter opens the tile's settings; only Space lifts it.
      keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space"] },
    }),
  );

  /** The cell under the tile's top-left corner, in the grid it is over. */
  function targetOf({ active, over }: Pick<DragMoveEvent, "active" | "over">): Target | null {
    const widget = byId.get(String(active.id));
    const area = over?.id as WidgetArea | undefined;
    const grid = area && elements.current[area];
    const tile = active.rect.current.translated;
    if (!widget || !area || !grid || !tile) return null;
    const box = grid.getBoundingClientRect();
    const step = cellSteps(grid);
    const { w, h } = widget;
    const x = Math.min(
      Math.max(0, Math.round((tile.left - box.left) / step.x)),
      HOME_GRID_COLUMNS - w,
    );
    const y = Math.min(
      Math.max(0, Math.round((tile.top - box.top) / step.y)),
      HOME_GRID_MAX_ROWS - h,
    );
    const others = grids[area].filter((other) => other.id !== widget.id).map(rectOf);
    return { x, y, w, h, id: widget.id, area, free: fitsAt({ x, y, w, h }, others) };
  }

  function resize(widget: HomeWidget, dims: { w: number; h: number }) {
    const others = grids[widget.area].filter((w) => w.id !== widget.id).map(rectOf);
    if (!fitsAt(rectOf({ ...widget, ...dims }), others)) {
      toast.error("There is not enough room for that size here. Move the tile first.");
      return;
    }
    onPlace(widget, dims);
  }

  const name = (id: string | number) => {
    const widget = byId.get(String(id));
    return widget ? widgetLabel(widget) : "Tile";
  };

  return (
    <TileDndContext.Provider
      value={{
        target,
        register: (area, element) => {
          if (element) elements.current[area] = element;
          else delete elements.current[area];
        },
        resize,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={collision}
        onDragStart={({ active }) => setDragging(byId.get(String(active.id)) ?? null)}
        onDragMove={(event) => setTarget(targetOf(event))}
        onDragEnd={(event) => {
          const next = targetOf(event);
          const widget = byId.get(String(event.active.id));
          setTarget(null);
          setDragging(null);
          if (widget && event.over?.id === TRASH && widget.type !== "boards") {
            onDelete(widget);
            return;
          }
          if (!widget || !next?.free) return;
          if (next.area === widget.area && next.x === widget.x && next.y === widget.y) return;
          // The Boards widget stays in the top grid (FR6).
          if (widget.type === "boards" && next.area !== "top") return;
          onPlace(widget, {
            x: next.x,
            y: next.y,
            ...(next.area !== widget.area && { area: next.area }),
          });
        }}
        onDragCancel={() => {
          setTarget(null);
          setDragging(null);
        }}
        accessibility={{
          screenReaderInstructions: {
            draggable:
              "Press Enter to edit. Press Space to pick up, the arrow keys to move one cell, Space to drop, Escape to cancel.",
          },
          announcements: {
            onDragStart: ({ active }) => `Picked up ${name(active.id)}.`,
            onDragOver: ({ over }) =>
              over?.id === TRASH
                ? "Over delete. Drop to delete."
                : over
                  ? `Over the ${over.id === "top" ? "top" : "board"} grid.`
                  : undefined,
            onDragEnd: ({ active, over }) =>
              over?.id === TRASH ? `${name(active.id)} deleted.` : `${name(active.id)} dropped.`,
            onDragCancel: ({ active }) => `Moving ${name(active.id)} was cancelled.`,
          },
        }}
      >
        {children}
        {/* The Boards widget is built in (FR6): it can never be deleted. */}
        {dragging && dragging.type !== "boards" && <DeleteZone />}
      </DndContext>
    </TileDndContext.Provider>
  );
}

const TRASH = "trash";

/** FR8: shows while a tile is dragged; dropping the tile on it deletes it (with Undo). */
function DeleteZone() {
  const { setNodeRef, isOver } = useDroppable({ id: TRASH });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "fixed bottom-6 left-1/2 z-30 flex h-14 -translate-x-1/2 items-center gap-2 border border-dashed px-6 text-sm font-medium shadow-window backdrop-blur-sm transition-colors duration-150",
        isOver
          ? "border-destructive bg-destructive/15 text-destructive"
          : "border-input bg-card/90 text-muted-foreground",
      )}
    >
      <Trash2 className="size-4" aria-hidden />
      Drop to delete
    </div>
  );
}

interface FreeGridProps {
  area: WidgetArea;
  widgets: HomeWidget[];
  editing: boolean;
  /** Empty rows below the tiles while editing, to drag into. */
  extraRows: number;
  onEdit: (widget: HomeWidget) => void;
  onDelete: (widget: HomeWidget) => void;
}

/** One 12-column free grid (the top grid or a board grid), a drop area of `TileDnd`. */
export function FreeGrid({ area, widgets, editing, extraRows, onEdit, onDelete }: FreeGridProps) {
  const dnd = useContext(TileDndContext);
  const { setNodeRef } = useDroppable({ id: area, disabled: !editing });
  const target = dnd?.target?.area === area ? dnd.target : null;

  // Enough rows for every tile, plus room below to drag into while editing.
  const bottom = Math.max(0, ...widgets.map((w) => rectOf(w).y + rectOf(w).h));
  // Fixed while dragging: a growing grid would push the grid below away from the pointer.
  const extra = editing ? extraRows : 0;
  const rows = Math.min(
    HOME_GRID_MAX_ROWS,
    Math.max(bottom, target ? target.y + target.h : 0) + extra,
  );

  return (
    <div
      ref={(element) => {
        setNodeRef(element);
        dnd?.register(area, element);
      }}
      data-tile-grid
      className="relative grid grid-cols-12 auto-rows-[0.5rem] gap-2"
      // Explicit rows so empty space below the tiles can be dropped into.
      style={{ gridTemplateRows: `repeat(${Math.max(rows, 1)}, ${ROW_REM}rem)` }}
    >
      {/* The background cells are two rows tall (1.5rem), so the grid stays calm. */}
      {editing &&
        Array.from({ length: Math.ceil(rows / 2) * HOME_GRID_COLUMNS }, (_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed background cells
            key={i}
            aria-hidden
            className="border border-dashed border-border/40"
            style={{
              gridColumn: (i % HOME_GRID_COLUMNS) + 1,
              gridRow: `${Math.floor(i / HOME_GRID_COLUMNS) * 2 + 1} / span ${Math.min(2, rows - Math.floor(i / HOME_GRID_COLUMNS) * 2)}`,
            }}
          />
        ))}
      {target && (
        <span
          aria-hidden
          className={cn(
            "border-2 border-dashed",
            target.free ? "border-ring bg-ring/10" : "border-destructive bg-destructive/10",
          )}
          style={{
            gridColumn: `${target.x + 1} / span ${target.w}`,
            gridRow: `${target.y + 1} / span ${target.h}`,
          }}
        />
      )}
      {widgets.map((widget) => (
        <WidgetTile
          key={widget.id}
          widget={widget}
          editing={editing}
          free
          onEdit={() => onEdit(widget)}
          onResize={(dims) => dnd?.resize(widget, dims)}
          onDelete={() => onDelete(widget)}
        />
      ))}
    </div>
  );
}
